import {
  Component,
  ViewChild,
  Input,
  ElementRef,
  ChangeDetectorRef,
  AfterViewInit,
  Inject,
  AfterViewChecked,
  ChangeDetectionStrategy,
  OnDestroy,
  NgZone,
  ViewEncapsulation,
  SimpleChanges,
  OnChanges,
  HostListener
} from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IS_BROWSER, IS_MOBILE } from '@app/modules/core/tokens/app.tokens';
import {
  CarouselDirection, PositionDots, StyleDots
} from '@app/modules/carousel/entities/carousel-direction';
import { HOTEL_GALLERY_TIMER } from '@ux/constants/animation-timer';
import { PaginationType } from '@design-system/components/molecules/pagination/entities/pagination.types';

const INTERVAL = 7000;

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.container.html',
  styleUrls: ['./carousel.container.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class CarouselContainer implements AfterViewInit, AfterViewChecked, OnDestroy, OnChanges {

  @Input() showDots: boolean = true;
  @Input() scroll: boolean = false;
  @Input() areDotsInside: boolean = false;
  @Input() dotsDelay: boolean = false;
  @Input() styleDots: StyleDots = StyleDots.Home;
  @Input() autoMoveItems: boolean = false;
  @Input() positionDots: PositionDots = PositionDots.Center;
  @Input() fullHeight: boolean = false;
  @Input() positionSpaceMedium: boolean = false;

  @ViewChild('carouselContent', { static: true }) content: ElementRef;
  @ViewChild('dotsItem', { static: true }) dotsItem: ElementRef;

  private unsubscribe$: Subject<void> = new Subject();
  private carouselDirection: CarouselDirection = CarouselDirection.Rigth;

  public dots: number[];
  public currentPosition: number = 0;

  constructor(
    @Inject(IS_BROWSER) public isBrowser: boolean,
    @Inject(IS_MOBILE) public isMobile: boolean,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isBrowser && changes['showDots'] && this.dotsItem && this.dotsItem.nativeElement) {
      if (this.showDots) {
        if (!this.dotsDelay) {
          this.dotsItem.nativeElement.classList.add('activate');
        } else {
          setTimeout(() =>
            this.dotsItem.nativeElement.classList.add('activate'), HOTEL_GALLERY_TIMER);
        }
      } else this.dotsItem.nativeElement.classList.remove('activate');
      this.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.autoMove();
    }
  }

  ngAfterViewChecked(): void {
    if (this.isBrowser) {
      this.dots = Array(this.content.nativeElement.children.length).fill(0);
      this.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.completeAutoMove();
  }

  @HostListener('scroll', ['$event']) onScroll(event: Event): void {
    const scrollLeft = event.target['scrollLeft'];
    const childrenWidth = event.target['children'][0].clientWidth;
    this.currentPosition = Math.round(scrollLeft / childrenWidth);
  }

  get pagenationType(): PaginationType {
    return this.styleHotelDots ? PaginationType.Base : PaginationType.Dot;
  }

  get styleHotelDots(): boolean {
    return this.styleDots === StyleDots.HotelsResults;
  }

  get styleUpsellDots(): boolean {
    return this.styleDots === StyleDots.Upsell;
  }

  get positionDotsRight(): boolean {
    return this.positionDots === PositionDots.Right;
  }

  get showLeft(): boolean {
    return this.content.nativeElement.scrollLeft > 0;
  }

  get showRight(): boolean {
    const { clientWidth, scrollLeft, scrollWidth } = this.content.nativeElement;
    return scrollLeft + clientWidth < scrollWidth;
  }

  public moveSlider(position: number): void {
    if (position > this.currentPosition) {
      this.carouselDirection = CarouselDirection.Rigth;
    } else {
      this.carouselDirection = CarouselDirection.Left;
    }
    let currentPosition = this.currentPosition;
    while (currentPosition !== position) {
      if (this.carouselDirection === CarouselDirection.Rigth) {
        currentPosition++;
      } else currentPosition--;
      if (!this.content.nativeElement) return;
      if (currentPosition >= this.content.nativeElement.children.length) {
        position = currentPosition = 0;
      }
      this.scrollCarouselTo(this.content.nativeElement.children[currentPosition]?.offsetLeft, .2);
    }
  }

  public moveCarouselByDot(event: { page: number }): void {
    if (event.page !== this.currentPosition + 1) {
      this.moveSlider(event?.page - 1);
    }
  }

  public onContentChange(event: MutationRecord): void {
    this.detectChanges();
  }

  private scrollCarouselTo(position: number, duration: number): void {
    this.content.nativeElement.scrollTo({
      left: position, top: top, behavior: 'smooth'
    });
  }

  private detectChanges(): void {
    this.changeDetector.detectChanges();
  }

  private autoMove(): void {
    if (this.autoMoveItems) {
      this.ngZone.runOutsideAngular(() => {
        timer(INTERVAL, INTERVAL).pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
          const newPosition = this.carouselDirection === CarouselDirection.Rigth ?
            this.currentPosition + 1 : this.currentPosition - 1;
          this.moveSlider(newPosition);
        });
      });
    }
  }

  private completeAutoMove(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get showCarouselDot(): boolean {
    return this.showDots && this.dots?.length > 1;
  }
}
